<script setup lang="ts">
    import { formatDateTime } from '@cwd/Shared/utils/formatDateTime';
    import { formatNumber } from '@cwd/Shared/utils/formatNumber';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { SegmentAccess } from './Segments.access';
    import { ContextAccess } from '@shared/Root/Context.access';
    import Form from '@cwd/shared/Components/Form/Form.vue';
    import { FormProps } from '@cwd/shared/components/Form/Form';
    import { bsAlert, AlertTypes } from '@shared/components/BsAlerts';
    import Loading from '@shared/components/Loading.vue';
    import Tabs, { tab } from '@shared/components/Tabs.vue';
    import { inject, onMounted, onUnmounted, reactive, watch, computed } from 'vue';
    import SegmentEditor from './SegmentEditor/SegmentEditor.vue';
    import { saveSegment, loadSegmentContactCount, copySegment } from './services/requests';
    import { setupTags } from '@shared/components/Tags/Tags';
    import TagsList from '@shared/components/Tags/TagsList.vue';
    import SegmentContactList from './Contacts/SegmentContactList.vue';
    import BSDropdownButton from '@shared/components/BSDropdownButton.vue';
    import Modal from '@shared/components/Modal.vue';
    import SingleSelect from '@shared/components/SingleSelect.vue';
    import { AccessControlList, CurrentObject } from '@cwd/Acl/Acl.access';
    import { hasAclWriteAccess } from '@cwd/Acl/useAclAccess';
    import AclEditor from '@cwd/Acl/AclEditor.vue';
    import Contact from '@views/Contact/Contact.vue';
    import SegmentCalculation from './Calculation/SegmentCalculation.vue';
    import { signalRService, initializeSignalR, SegmentCalculationResult } from '@shared/services/signalR/signalr-service';
    import Alert from '@shared/components/BsAlert.vue';
    import { useRoute, useRouter } from 'vue-router';
    import { FeatureService } from '@shared/directives/Feature.service';

    const router = useRouter();
    const route = useRoute();
    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const contextAccess: ContextAccess = injectorAccess.injector.get(ContextAccess);
    const segmentAccess: SegmentAccess = injectorAccess.injector.get(SegmentAccess);
    const featureService: FeatureService = injectorAccess.injector.get(FeatureService);

    const tags = setupTags();

    const props = defineProps<{
        segmentId: string;
    }>();

    const state = reactive<{
        isBusy: boolean;
        formReady: boolean;
        segmentForm: FormProps;
        totalContactCount: number | null;
        resultContactCount: number;
        showModal: boolean;
        contactId: string | null;
        tagsLoaded: boolean;
        autoCalculate: boolean;
        requestId: string;
        showExportToExistingListModal: boolean;
        selectedExportListId: string | null;
    }>({
        isBusy: false,
        formReady: false,
        segmentForm: {
            disableSubmit: false,
            alert: null,
            data: {},
        },
        totalContactCount: null,
        resultContactCount: 0,
        showModal: false,
        contactId: null,
        tagsLoaded: false,
        autoCalculate: false,
        requestId: '',
        showExportToExistingListModal: false,
        selectedExportListId: null as string | null,
    });

    function toggleAclModal() {
        state.showModal = !state.showModal;
    }

    function onSaveAcl(acl: AccessControlList) {
        toggleAclModal();
        if (!segmentAccess.state?.segment?.AccessControlList) {
            if (!segmentAccess.state.segment) return;
            segmentAccess.state.segment.AccessControlList = [];
        } else segmentAccess.state.segment.AccessControlList = acl as unknown as AccessControlList[];
    }

    onMounted(async () => {
        await segmentAccess.init(props.segmentId).then(() => {
            setupFormData();

            startCalculation();
        });

        if (route.query.copySuccess === 'true') {
            bsAlert();

            const { copySuccess, ...rest } = route.query;
            router.replace({ query: rest });
        }

        watch(
            () => tags.state.loaded,
            (newVal) => {
                if (newVal && state.segmentForm) {
                    state.segmentForm.data.Tags.multiSelect!.loading = false;
                    state.segmentForm.data.Tags.multiSelect!.items = tags.state.list;
                }
            }
        );
    });

    onUnmounted(() => {
        segmentAccess.cleanupListeners();
    });

    async function saveInfo(event: Event, formData: any) {
        if (!featureService.hasFeature('Segments')) return;
        if (state.isBusy === true || !segmentAccess.state.segment) return;

        state.isBusy = true;
        state.segmentForm.setInitData = false;

        Object.assign(segmentAccess.state.segment, formData);

        await saveSegment(segmentAccess.state.segment)
            .then((data) => {
                state.segmentForm.setInitData = true;

                bsAlert({
                    title: $Resources.AlertIssueSavedHeader,
                    content: $Resources.AlertSegmentSavedBody,
                });
            })
            .catch(() => {
                bsAlert({
                    title: $Resources.ErrorHeading,
                    content: $Resources.ErrorLabel,
                    type: AlertTypes.Danger,
                });
            })
            .finally(() => (state.isBusy = false));
    }

    function setupFormData(): void {
        state.segmentForm.data = {
            Name: {
                modelValue: segmentAccess.state.segment?.Name,
                required: true,
                input: {
                    label: $Resources.SegmentNameLabel,
                },
            },
            Description: {
                modelValue: segmentAccess.state.segment?.Description,
                input: {
                    label: $Resources.SegmentDescriptionLabel,
                },
            },
            Tags: {
                modelValue: segmentAccess.state.segment?.Tags,
                multiSelect: {
                    label: $Resources.TagsLabel,
                    selectProp: 'TagId',
                    displayProp: 'Title',
                    items: tags.state.list || [],
                    loading: !tags.state.loaded,
                    infoText: $Resources.TagsHelpText,
                },
            },
        };

        state.formReady = true;
    }

    const getContactCount = async () => {
        await loadSegmentContactCount().then((data) => {
            state.totalContactCount = data;
        });
    };

    const segmentPreviewPercentage = () => {
        if (
            state.totalContactCount == null ||
            segmentAccess.state.segmentPreview?.Count == null ||
            state.totalContactCount <= 0 ||
            segmentAccess.state.segmentPreview?.Count <= 0
        )
            return 0;

        return (segmentAccess.state.segmentPreview.Count / state.totalContactCount) * 100;
    };

    const segmentPreviewListHeaderText = () => {
        if (segmentAccess.state.segmentPreview == null) return '';

        return $Resources.SegmentPreviewContactsHeading.replace('{{count}}', formatNumber(segmentAccess.state.segmentPreview.Result.length));
    };

    const segmentPreviewListHeaderBody = () => {
        if (segmentAccess.state.backgroundCalculationResult == null) return '';

        return $Resources.SegmentExecutionTimeText.replace('{{time}}', segmentAccess.state.backgroundCalculationResult.ExecutionTime);
    };

    const createAndAddNewTag = (tagName: string) => {
        tags.create(tagName, state.segmentForm.data.Tags.modelValue);
    };

    let scrollPosition = 0;

    const showContact = (contactId: string) => {
        scrollPosition = document.documentElement.scrollTop;
        setTimeout(() => {
            state.contactId = contactId;
        }, 0);
    };

    const closeContact = () => {
        state.contactId = null;
        setTimeout(() => {
            document.documentElement.scrollTop = scrollPosition;
        }, 0);
    };

    async function startCalculation() {
        await segmentAccess.startBackgroundCalculation(false);
    }

    async function viewTabOpened() {
        if (segmentAccess.state.segmentPreview == null || segmentAccess.state.hasSegmentChanges) {
            await startCalculation();
        }
    }

    const toJourney = () => {
        router.push({ name: 'JourneyViewport', params: { JourneyId: segmentAccess.state.segment?.OwnedByJourney?.JourneyId } });
    };

    const toEvent = () => {
        const eventId = segmentAccess.state.segment?.OwnedByEvent?.EventId;
        if (!eventId) return;
        router.push({ name: 'EventEditorRoot', params: { eventId } });
    };

    const handleCopySegment = async () => {
        if (!featureService.hasFeature('Segments')) return;
        if (state.isBusy) return;

        const segmentId = segmentAccess.state.segment?.SegmentId;
        if (!segmentId) return;

        state.isBusy = true;

        return copySegment(segmentId)
            .then((data) => {
                router.push({
                    name: 'SegmentEdit',
                    params: { segmentId: data.SegmentId },
                    query: { copySuccess: 'true' },
                });
            })
            .catch(() => {
                bsAlert({
                    type: AlertTypes.Danger,
                });
            })
            .finally(() => (state.isBusy = false));
    };

    const openExportToExistingListModal = async () => {
        if (!segmentAccess.state.lists) {
            await segmentAccess.getLists();
        }
        state.selectedExportListId = null;
        state.showExportToExistingListModal = true;
    };

    const confirmExportToExistingList = async () => {
        if (!state.selectedExportListId) return;
        state.showExportToExistingListModal = false;
        await segmentAccess.exportToExistingList(state.selectedExportListId);
    };

    const disableButtons = computed(() => {
        return (
            state.isBusy ||
            !featureService.hasFeature('Segments') ||
            !hasAclWriteAccess(segmentAccess.state.segment?.AccessControlList, contextAccess.state.currentUser?.UserId)
        );
    });
</script>

<template>
    <div class="container-fluid p-lg-t" v-show="!state.contactId">
        <p class="fw-600">
            <router-link :to="{ name: 'Segments' }">
                <span class="glyphicon glyphicon-chevron-left c-light" aria-hidden="true"></span>
                {{ $Resources.SegmentsButton }}
            </router-link>
        </p>

        <Alert
            class="m-lg-t"
            :show="!featureService.hasFeature('Segments')"
            :type="AlertTypes.Danger"
            :title="$Resources.FeatureLockedHeading"
            :content="$Resources.SegmentsFeatureLockedBody"
            :dismissible="false"></Alert>

        <Alert
            class="m-lg-t"
            :show="segmentAccess.state?.segment?.OwnedByJourney != null"
            :type="AlertTypes.Info"
            :title="$Resources.SegmentOwnedByJourneyHeading"
            :content="
                ($Resources.SegmentOwnedByJourneyBody || '').replace(
                    '{{JourneyName}}',
                    '<strong>' + segmentAccess.state.segment?.OwnedByJourney?.Title + '</strong>'
                )
            "
            :buttonText="$Resources.ViewJourneyButton"
            :dismissible="false"
            @buttonClick="toJourney()"></Alert>

        <Alert
            class="m-lg-t"
            :show="segmentAccess.state?.segment?.OwnedByEvent != null"
            :type="AlertTypes.Info"
            :title="$Resources.SegmentOwnedByEventHeading"
            :content="
                ($Resources.SegmentOwnedByEventBody || '').replace('{{EventName}}', '<strong>' + segmentAccess.state.segment?.OwnedByEvent?.Name + '</strong>')
            "
            :buttonText="$Resources.ViewEventButton"
            :dismissible="false"
            @buttonClick="toEvent()"></Alert>

        <div v-if="segmentAccess.state.segment != null">
            <h1>{{ segmentAccess.state.segment.Name }}</h1>
            <p>{{ segmentAccess.state.segment.Description }}</p>

            <TagsList
                :readonly="true"
                v-show="segmentAccess.state.segment.Tags.length > 0"
                :tags="segmentAccess.state.segment.Tags"
                :selectedIds="tags.state.selectedIds"></TagsList>

            <p class="meta">
                <span class="glyphicon glyphicon-time"></span>
                {{ $Resources.CreatedLabel }}
                <time :datetime="segmentAccess.state.segment.CreatedAt">{{ formatDateTime(segmentAccess.state.segment.CreatedAt) }}</time>
                <i class="fa fa-pencil" aria-hidden="true"></i>
                {{ $Resources.ModifiedLabel }}
                <time :datetime="segmentAccess.state.segment.Modified">{{ formatDateTime(segmentAccess.state.segment.Modified) }}</time>
                <span class="glyphicon glyphicon-user"></span>
                {{ $Resources.ModifiedByLabel }}
                <span>{{ segmentAccess.state.segment.ModifiedBy.Name }}</span>
            </p>
        </div>

        <tabs navClass="tabs list-unstyled">
            <template v-slot:right-content>
                <div class="btn-group m-lg-b gap-sm">
                    <button type="button" class="btn btn-default" @click="toggleAclModal">
                        <span class="glyphicon glyphicon-lock"></span>
                        {{ $Resources.ListNavAclLabel }}
                    </button>

                    <button type="button" class="btn btn-default" :disabled="disableButtons" @click="handleCopySegment">
                        <span class="glyphicon glyphicon-duplicate"></span>
                        {{ $Resources.CloneBlockButton }}
                    </button>

                    <BSDropdownButton :closeOnClickInside="true" menuClass="dropdown-menu-right" :disabled="disableButtons">
                        <template #toggle>
                            {{ $Resources.ExportButton }}
                            <span class="caret m-xs-l"></span>
                        </template>
                        <template #content>
                            <li>
                                <a @click="segmentAccess.exportToNewList" href="javascript:void(0)">
                                    {{ $Resources.ExportToNewListButton }}
                                </a>
                            </li>
                            <li>
                                <a @click="openExportToExistingListModal" href="javascript:void(0)">
                                    {{ $Resources.ExportToExistingListButton }}
                                </a>
                            </li>
                            <li>
                                <a @click="segmentAccess.exportToFile" href="javascript:void(0)">
                                    {{ $Resources.ExportToFileButton }}
                                </a>
                            </li>
                        </template>
                    </BSDropdownButton>
                </div>
            </template>
            <tab :title="$Resources.SegmentInfoTab">
                <Form
                    v-if="state.formReady"
                    class="fieldset-grid max-800"
                    :data="state.segmentForm.data"
                    :disableSubmit="segmentAccess.state?.segment?.OwnedByJourney != null || disableButtons"
                    :setInitData="state.segmentForm.setInitData"
                    @update:isValid="(key, newVal) => (state.segmentForm!.data[key].isValid = newVal)"
                    @update:modelValue="(key, newVal) => (state.segmentForm.data[key].modelValue = newVal)"
                    @onSubmit="saveInfo"
                    @newItem="(newVal) => createAndAddNewTag(newVal)"></Form>
            </tab>
            <tab :title="$Resources.SegmentEditTab">
                <SegmentEditor :segment-id="props.segmentId"></SegmentEditor>
            </tab>
            <tab :title="$Resources.SegmentViewTab" @tabClick="viewTabOpened()">
                <template v-if="!segmentAccess.state.isBackgroundCalculating">
                    <div v-if="segmentAccess.state.segmentPreview" class="stat-highlights panel panel-default">
                        <div class="row no-gutter d-flex flex-wrap">
                            <div class="flex-col col-md-6 col-sm-6 col-xs-12 col-print-6">
                                <div class="stat-highlights-body ug-bars">
                                    <p>{{ $Resources.SegmentSizeLabel }}</p>
                                    <p class="value">
                                        {{ formatNumber(segmentAccess.state.segmentPreview?.Count || 0) }}
                                    </p>
                                </div>
                            </div>

                            <div class="flex-col col-md-6 col-sm-6 col-xs-12 col-print-6">
                                <div class="stat-highlights-body ug-bars">
                                    <p>{{ $Resources.SegmentPercentageOfContactsLabel }}</p>
                                    <p class="value">{{ formatNumber(segmentPreviewPercentage()) }}%</p>
                                    <div class="bar">
                                        <span class="bar-value" :style="'width: ' + segmentPreviewPercentage() + '%;'"></span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <h2 class="h3" v-if="segmentAccess.state.segmentPreview">
                        {{ segmentPreviewListHeaderText() }}
                    </h2>

                    <p class="meta m-lg-b" v-if="segmentAccess.state.segmentPreview">
                        {{ segmentPreviewListHeaderBody() }}
                    </p>

                    <SegmentContactList @navigateToContact="(newVal: string) => showContact(newVal)" />
                </template>

                <!-- Initial loading - hide most UI -->
                <Loading :show="segmentAccess.state.isBackgroundCalculating" loadingStyle="progress-bar"></Loading>
                <Loading :show="segmentAccess.state.isLoadingMoreContacts" loadingStyle="progress-bar" :fixed="true"></Loading>
            </tab>
        </tabs>

        <AclEditor
            :selected-groups="
                segmentAccess.state.segment?.AccessControlList?.map(
                    (acl) =>
                        ({
                            ...acl,
                            GroupId: acl.PrincipalId,
                            AccessControlList: acl,
                        }) as CurrentObject
                ) || []
            "
            type="Segment"
            :objectId="segmentId"
            :open="state.showModal"
            @save="onSaveAcl"
            @close="toggleAclModal" />
    </div>

    <div v-show="state.contactId">
        <Contact :contactId="state.contactId" :backBtn="true" @goBack="closeContact"></Contact>
    </div>

    <Loading :show="state.isBusy" loadingStyle="progress-bar" :fixed="true"></Loading>

    <Modal :show="state.showExportToExistingListModal" :title="$Resources.ExportToExistingListTitle" @close="state.showExportToExistingListModal = false">
        <template #content>
            <SingleSelect
                :items="segmentAccess.state.lists || []"
                :modelValue="state.selectedExportListId"
                selectProp="ListId"
                displayProps="Name"
                :label="$Resources.SelectListLabel"
                @update:modelValue="(val) => (state.selectedExportListId = val)" />
        </template>
        <template #button>
            <button type="button" class="btn btn-primary" :disabled="!state.selectedExportListId" @click="confirmExportToExistingList">
                {{ $Resources.ExportButton }}
            </button>
        </template>
    </Modal>
</template>
